<app-tools-catalogue-menu
  [breadcrumbLinks]="breadcrumbLinks"
></app-tools-catalogue-menu>

<div class="container m-t-alt">
  <div class="columns">
    <div class="column is-10 is-offset-1">
      <div class="card has-background-white mb-6">
        <form [formGroup]="form" (submit)="submit(form.value)">
          <h1 class="title is-2 mt-6">
            Submit a use case to the Catalogue of Tools & Metrics for
            Trustworthy AI
          </h1>
          <div class="content is-small">
            <p class="mb-0">
              <u>Make sure your submission is complete </u>
            </p>
            <p class="mt-0">
              Please note that the submission should be<b>
                completed using full sentences and paragraphs.</b
              >
            </p>

            <p class="mb-0"><u>Explain through examples</u></p>
            <p class="mt-0">
              Each use case should explain an experience using a tool for
              trustworthy AI for a specific goal in a given context. Some
              examples include using a
              <b>
                transparency tool for fraud prevention or a bias-mitigation tool
                for disease detection.</b
              >
            </p>
            <p>
              Use cases will be linked to that tool in the catalogue to serve as
              practical guidance.
            </p>

            <p class="mb-0"><u>Tell a story </u></p>

            <p class="mt-0">
              Use descriptive information and contextualise the benefits and
              issues that made up your experience. Language should be concise,
              clear and engaging.
            </p>
            <p class="mb-0"><u>Neutrality is key </u></p>

            <p class="mt-0">
              This is a collective-learning exercise for the benefit of the
              global AI community. Please share objective insights and good
              practices in an honest yet professional manner and avoid focusing
              on individual or organisational interests.
            </p>
            <p>
              Please <b> maintain a neutral</b> tone and avoid promotional
              language. All statements must be objective, as appropriate for an
              international organisation. This approach makes the use case’s
              content accessible to broader, non-expert audiences.
            </p>

            <p class="mb-0">
              <u>Include rich and reusable content</u>
            </p>

            <p class="mt-0 mb-5">
              The more complete the use case, the more likely it is that people
              will read it. We also highly recommend including rich content such
              as charts, graphs, hyperlinks, code and images.
            </p>
          </div>

          <div class="field mb-5">
            <label class="is-size-7" for="title-input"
              >Title of the use case<span class="has-text-danger"
                >*</span
              ></label
            >
            <div class="control mb-0">
              <input
                class="input is-large"
                [ngClass]="{
                  'is-danger-bis': showErrors && invalidInputs.includes('title')
                }"
                type="text"
                formControlName="title"
                id="title-input"
                placeholder="Title of the use case..."
              />
            </div>
          </div>
          <div class="field mb-5">
            <label class="is-size-7" for="title-input"
              >Tool concerned<span class="has-text-danger">*</span></label
            >
            <p class="is-size-8 mt-0 mb-2">
              Please select the tool that this use case illustrates.
            </p>
            <div class="control mb-0">
              <input
                class="input is-large"
                readonly
                type="text"
                [value]="tool.name"
                *ngIf="tool"
              />
              <app-select-autocomplete-input
                [searchFunction]="searchToolFunction"
                [isError]="showErrors && invalidInputs.includes('toolId')"
                (selectionChanged)="onToolSelected($event)"
                noResultContent="Can't find your tool in the list ? Submit it here"
                noResultLink="/catalogue/tools/submit"
                *ngIf="!tool"
              ></app-select-autocomplete-input>
            </div>
          </div>
          <div class="field mb-5">
            <label class="is-size-7" for="title-input"
              >Short sentence describing the use case (max 30 words)<span
                class="has-text-danger"
                >*</span
              ></label
            >
            <div class="control mb-0">
              <input
                class="input is-large"
                type="text"
                formControlName="shortSentence"
                [ngClass]="{
                  'is-danger-bis':
                    showErrors && invalidInputs.includes('shortSentence')
                }"
              />
            </div>
          </div>

          <div class="field mb-5">
            <label class="is-size-7" for="title-input">Image</label>
            <div
              class="is-flex is-flex-wrap-wrap is-align-items-flex-start is-justify-content-flex-start"
            >
              <app-image-input
                placeholder="Upload an image for this use case"
                helpText="An image to illustrate this use case"
                resourceName="tool-use-cases"
                (valueChanged)="onFieldChanged($event, 'image')"
                *ngIf="!form.value.defaultImage"
              ></app-image-input>

              <div
                class="has-current-file is-flex is-align-items-center is-justify-content-space-between mt-1 has-background-white-ter pl-1"
                *ngIf="form.value.defaultImage"
              >
                <img
                  width="46"
                  class="py-1 is-block"
                  [src]="
                    storagePath + form.value.defaultImage + '-thumbnail.jpg'
                  "
                />
                <span
                  class="file-cta file-remove"
                  (click)="onFieldChanged('', 'defaultImage')"
                  (keydown)="onFieldChanged('', 'defaultImage')"
                  tabindex="0"
                  ><a><i class="fa fa-times is-size-5"></i></a
                ></span>
              </div>

              <a
                (click)="showImageGalleryModal = true"
                (keydown)="showImageGalleryModal = true"
                tabindex="0"
                class="button is-ghost button--open-gallery sans has-text-link"
                *ngIf="!form.value.defaultImage && !form.value.image"
              >
                <i class="fa fa-image mr-2 mb"></i>
                <span>Or pick an image from our library</span>
              </a>
            </div>
          </div>

          <div class="field mb-5">
            <label class="is-size-7"
              >Description<span class="has-text-danger">*</span></label
            >
            <p class="is-size-8 mt-0 mb-2">
              A description that provides a background and overview of the use
              case (maximum 1000 words - we recommend you submit at least 2-3
              full paragraphs). Feel free to include images, graphs and charts
              to illustrate the use case as needed
            </p>
            <div class="control mb-4">
              <app-rich-text-input
                uniqueId="description"
                placeholder="Overview on the usage (maximum 1000 words)..."
                (valueChanged)="onFieldChanged($event.value, 'description')"
                [showErrors]="showErrors"
                [required]="true"
                [maxWords]="1000"
              ></app-rich-text-input>
            </div>
          </div>

          <div class="field mb-5">
            <label class="is-size-7"
              >Describe the benefits of using the tool in this use case. Please
              submit at least one full paragraph.</label
            >
            <div class="control mb-4">
              <app-rich-text-input
                uniqueId="benefits"
                placeholder="Describe benefits of using the tool in this use case..."
                (valueChanged)="onFieldChanged($event.value, 'benefits')"
                [showErrors]="showErrors"
              ></app-rich-text-input>
            </div>
          </div>

          <div class="field mb-5">
            <label class="is-size-7"
              >Describe the shortcomings of using the tool in this use case.
              Please submit at least one full paragraph.
            </label>
            <div class="control mb-4">
              <app-rich-text-input
                uniqueId="shortcomings"
                placeholder="Describe shortcomings of using the tool in this use case..."
                (valueChanged)="onFieldChanged($event.value, 'shortcomings')"
                [showErrors]="showErrors"
              ></app-rich-text-input>
            </div>
          </div>

          <div class="field mb-5">
            <label class="is-size-7"
              >Outline any learnings or advice for using the tool in a similar
              context. If applicable, please submit at least one full
              paragraph.</label
            >
            <div class="control mb-4">
              <app-rich-text-input
                uniqueId="learnings"
                placeholder="Outline any learnings or advice for using the tool in a similar context..."
                (valueChanged)="onFieldChanged($event.value, 'learnings')"
                [showErrors]="showErrors"
              ></app-rich-text-input>
            </div>
          </div>

          <div class="field mb-6">
            <label class="is-size-7"
              >Comparison with other tools. If applicable, please submit at
              least one full paragraph</label
            >

            <p class="is-size-8 mt-0 mb-2">
              Did you try other tools for this use case? If so, how does this
              tool compare?
            </p>

            <div class="control mb-4">
              <app-rich-text-input
                uniqueId="comparison"
                placeholder="Comparison with other tools..."
                (valueChanged)="onFieldChanged($event.value, 'comparison')"
                [showErrors]="showErrors"
              ></app-rich-text-input>
            </div>
          </div>

          <!-- Taxonomy groups. -->
          <ng-container *ngFor="let taxonomyGroup of taxonomyGroups">
            <h3 class="mt-0 mb-1 title is-6 is-sans is-uppercase is-bold">
              {{ taxonomyGroup.name }}
            </h3>
            <p class="mt-0 mb-2 is-size-6" *ngIf="taxonomyGroup.description">
              {{ taxonomyGroup.description }}
            </p>

            <div class="columns is-multiline mb-4">
              <div
                class="column is-6 pt-5 pb-0"
                *ngFor="let taxonomyInput of taxonomyGroup.taxonomies"
              >
                <label class="is-size-7" for="excerpt-input">{{
                  taxonomyInput.label
                }}</label>
                <app-multi-tags-input
                  [values]="taxonomyInput.options"
                  [addNewOptionFunction]="taxonomyInput.createFunction"
                  [placeholder]="taxonomyInput.placeholder"
                  (selectedValuesChanged)="
                    onFieldChanged($event, taxonomyInput.propName)
                  "
                  [maxSelectedItems]="
                    taxonomyInput.maxSelectedItems
                      ? taxonomyInput.maxSelectedItems
                      : 99
                  "
                  *ngIf="taxonomyInput.options"
                >
                </app-multi-tags-input>
              </div>
            </div>
            <hr class="my-6" />
          </ng-container>

          <!-- Submitter information -->

          <div class="columns">
            <div class="column is-6">
              <h3 class="mt-0 mb-1 title is-6 is-sans is-uppercase is-bold">
                Email<span class="has-text-danger">*</span>
              </h3>

              <div class="field mb-5">
                <label class="is-size-7 help my-1" for="email-input">
                  We will use this to contact you if we have any further
                  questions related to your submission and to allow you to make
                  updates. Your email will not be shared nor made public and we
                  will not use it for other purposes. See our
                  <a href="https://www.oecd.org/privacy/" target="_blank"
                    >privacy policy</a
                  >
                  for more information.</label
                >
                <div class="control mb-4">
                  <input
                    [ngClass]="{
                      'is-danger-bis':
                        showErrors && invalidInputs.includes('email')
                    }"
                    class="input is-large"
                    type="email"
                    formControlName="email"
                    id="email-input"
                    placeholder="your-email@organization.com"
                  />
                </div>
                <p
                  *ngIf="showErrors && invalidInputs.includes('email')"
                  class="has-text-danger-bis mt-0 is-size-7"
                >
                  A valid email is required.
                </p>
              </div>
            </div>
            <div class="column is-6">
              <h3 class="mt-0 mb-1 title is-6 is-sans is-uppercase is-bold">
                Submitter<span class="has-text-danger">*</span>
              </h3>

              <div class="field mb-5">
                <label class="is-size-7 my-1 help" for="submitter-input">
                  Organisation or entity submitting the use case.</label
                >
                <div class="control mb-4">
                  <input
                    [ngClass]="{
                      'is-danger-bis':
                        showErrors && invalidInputs.includes('submitter')
                    }"
                    class="input is-large"
                    type="text"
                    formControlName="submitter"
                    id="submitter-input"
                    placeholder="Organisation or entity submitting the use case..."
                  />
                </div>
              </div>
            </div>
          </div>

          <hr class="mb-6 mt-5" />

          <div class="field mb-5">
            <h3 class="mt-0 mb-1 title is-6 is-sans is-uppercase is-bold">
              Disclosures for contributors
            </h3>
            <div class="control mb-4 mt-1">
              <label
                class="checkbox-input mr-4"
                for="consent-publication-delay-input"
              >
                Once you submit tools, metrics, or use cases, we will review
                them and, if accepted, post them online. Please understand that
                due to the sheer volume of submissions, the process may take
                several days and communicating about the status of each
                submission may not be possible.
                <span class="has-text-danger">*</span>
                <input
                  type="checkbox"
                  formControlName="consentPublicationDelay"
                  id="consent-publication-delay-input"
                />
                <span class="checkmark"></span>
              </label>
            </div>
            <div class="control mb-4 mt-1">
              <label class="checkbox-input mr-4" for="consent-input">
                By checking this box, I certify that all information included in
                this Submission Form is true to the best of my knowledge and
                that OECD has the non-exclusive, sub-licensable right to
                publish, copy, reproduce, distribute, perform, translate, and
                make derivative works from the information and material
                provided. For more details, please see
                <a
                  href="https://www.oecd.org/termsandconditions/"
                  target="_blank"
                  >OECD's Terms and Conditions</a
                >
                and
                <a
                  href="https://oecd.ai/en/catalogue-disclosures"
                  target="_blank"
                  >Catalogue disclosures</a
                >. <span class="has-text-danger">*</span>
                <input
                  type="checkbox"
                  formControlName="consent"
                  id="consent-input"
                />
                <span class="checkmark"></span>
              </label>
            </div>
            <p
              *ngIf="showErrors && invalidInputs.includes('consent')"
              class="has-text-danger-bis mt-0 is-size-7"
            >
              Your consent is mandatory to submit the tool.
            </p>
          </div>

          <div class="has-text-center is-flex is-justify-content-center pt-5">
            <re-captcha
              formControlName="recaptchaReactive"
              required
              [siteKey]="captchaKey"
            ></re-captcha>
          </div>
          <div class="level">
            <div class="level-left">
              <p class="level-item is-size-7">
                After you click on 'Submit', the OECD Secretariat will review
                your submission. 
              </p>
            </div>
            <div class="level-right">
              <div class="level-item">
                <button
                  class="button is-link"
                  [ngClass]="{
                    'is-loading': loadingSubmit
                  }"
                >
                  Submit use case
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</div>
<app-image-gallery-modal
  (modalClosed)="
    onFieldChanged($event, 'defaultImage'); showImageGalleryModal = false
  "
  *ngIf="showImageGalleryModal"
></app-image-gallery-modal>
<app-tools-catalogue-disclaimer></app-tools-catalogue-disclaimer>
